import { parseRecipeSpec } from './recipe_spec';

export const EXPERIENCE_KINDS = ['mealplan', 'checklist', 'itinerary', 'comparison', 'studyplan', 'projectguide', 'eventplan'];
const isObject = value => !!value && typeof value === 'object' && !Array.isArray(value);
const text = (value, max = 160) => typeof value === 'string' ? value.trim().slice(0, max) : '';
const id = value => typeof value === 'string' && /^[a-zA-Z0-9][a-zA-Z0-9_-]{0,63}$/.test(value) ? value : null;
const list = (value, max, parse, required = false) => {
  if (value === undefined && !required) return [];
  if (!Array.isArray(value) || value.length > max || (required && !value.length)) throw new Error('Invalid list');
  return value.map(entry => { const parsed = parse(entry); if (parsed == null) throw new Error('Invalid entry'); return parsed; });
};
const stringList = (value, max = 8) => list(value, max, entry => text(entry, 300) || null);
const unique = values => new Set(values).size === values.length;
export function safeExperienceUrl(value) {
  if (typeof value !== 'string' || value.length > 1500) return '';
  try {
    const url = new URL(value);
    return ['https:', 'http:'].includes(url.protocol) && url.hostname && !url.username && !url.password ? url.href : '';
  } catch (_) { return ''; }
}

// Reject malformed/oversized structures atomically. Never infer a card from
// arbitrary JSON or silently discard required rows of a plan.
export function parseExperienceSpec(raw, kind) {
  if (!EXPERIENCE_KINDS.includes(kind) || typeof raw !== 'string' || raw.length > 120000) return null;
  try {
    const source = JSON.parse(raw);
    if (!isObject(source) || source.version !== 1 || !id(source.id) || !text(source.title) || (source.kind && source.kind !== kind)) return null;
    const result = {
      kind, version: 1, id: source.id, title: text(source.title, 120), description: text(source.description, 400),
      summary: list(source.summary, 3, item => isObject(item) && text(item.label, 30) && text(item.value, 60) ? { label: text(item.label, 30), value: text(item.value, 60) } : null),
      tips: stringList(source.tips), equipment: stringList(source.equipment, 12),
      sections: [], supplies: [], options: [], criteria: [], quiz: [], recommendation: null,
    };
    if (!unique(result.summary.map(item => item.label))) return null;
    const seen = new Set();
    const parseItem = item => {
      if (!isObject(item) || !id(item.id) || !text(item.title) || seen.has(item.id)) return null;
      seen.add(item.id);
      const recipe = item.recipe === undefined ? null : parseRecipeSpec(JSON.stringify(item.recipe));
      if (item.recipe !== undefined && (kind !== 'mealplan' || !recipe)) return null;
      return {
        id: item.id, title: text(item.title), detail: text(item.detail, 500), time: text(item.time, 80),
        location: text(item.location, 240), travel: text(item.travel, 100), owner: text(item.owner, 80),
        url: safeExperienceUrl(item.url), recipe,
      };
    };
    if (kind === 'comparison') {
      result.criteria = list(source.criteria, 10, item => text(item, 60) || null, true);
      if (!unique(result.criteria)) return null;
      result.options = list(source.options, 4, item => {
        if (!isObject(item) || !id(item.id) || !text(item.title)) return null;
        const values = list(item.values, 10, value => text(value, 240) || null, true);
        if (values.length !== result.criteria.length) return null;
        return { id: item.id, title: text(item.title, 120), detail: text(item.detail, 240), values, pros: stringList(item.pros, 5), cons: stringList(item.cons, 5), url: safeExperienceUrl(item.url) };
      }, true);
      if (result.options.length < 2 || !unique(result.options.map(item => item.id))) return null;
      if (source.recommendation !== undefined) {
        if (!isObject(source.recommendation) || !result.options.some(item => item.id === source.recommendation.optionId) || !text(source.recommendation.reason)) return null;
        result.recommendation = { optionId: source.recommendation.optionId, reason: text(source.recommendation.reason, 500) };
      }
    } else {
      result.sections = list(source.sections, 14, section => {
        if (!isObject(section) || !text(section.title, 80)) return null;
        if (section.style !== undefined && !['timeline', 'checklist'].includes(section.style)) return null;
        return { title: text(section.title, 80), style: kind === 'eventplan' && section.style === 'checklist' ? 'checklist' : kind, items: list(section.items, 40, parseItem, true) };
      }, true);
      if (seen.size > 80) return null;
    }
    result.supplies = list(source.supplies, 12, group => {
      if (!isObject(group) || !text(group.title, 80)) return null;
      return { title: text(group.title, 80), items: list(group.items, 40, parseItem, true) };
    });
    if (seen.size > 120) return null;
    if (source.quiz !== undefined && kind !== 'studyplan') return null;
    result.quiz = list(source.quiz, 5, question => {
      if (!isObject(question) || !id(question.id) || !text(question.question, 300)) return null;
      const choices = list(question.choices, 5, value => text(value, 160) || null, true);
      if (choices.length < 2 || !unique(choices) || !Number.isInteger(question.answer) || question.answer < 0 || question.answer >= choices.length) return null;
      return { id: question.id, question: text(question.question, 300), choices, answer: question.answer, explanation: text(question.explanation, 500) };
    });
    if (!unique(result.quiz.map(question => question.id))) return null;
    return result;
  } catch (_) { return null; }
}

// Stable across reordering and revisions; changed instructions must be checked
// again. Store only a fingerprint and completion, never generated private text.
export function experienceItemKey(item) {
  const content = [item.id, item.title, item.detail, item.time, item.location, item.owner].join('\u001f');
  let hash = 2166136261;
  for (let i = 0; i < content.length; i += 1) hash = Math.imul(hash ^ content.charCodeAt(i), 16777619);
  return `${item.id}-${(hash >>> 0).toString(36)}`;
}
export function experienceProgressItems(spec) {
  const items = spec.sections.flatMap(section => ['checklist', 'studyplan', 'projectguide'].includes(section.style) ? section.items : []);
  return [...items, ...spec.supplies.flatMap(group => group.items)];
}
